<script setup lang="ts">
import type { PopoverPositionType } from '#components/popover/popover.model';
import { Float, FloatArrow } from '@headlessui-float/vue';
import { Popover as HuiPopover, PopoverPanel as HuiPopoverPanel } from '@headlessui/vue';
import { computed, ref, useSlots } from 'vue';

const {
  position = 'bottom',
  show = 'none',
  className = undefined
} = defineProps<{
  position?: PopoverPositionType;
  className?: string;
  // props to show the popover programmatically
  show?: 'hidden' | 'visible' | 'none';
}>();
const openRef = ref(false);
const timeOutRef = ref();
const slots = useSlots();

const isOpen = computed(() => {
  if (show === 'none') {
    return openRef.value;
  }

  return show === 'visible';
});

const hasContentSlot = computed(() => {
  return !!slots.content;
});

const handleEnter = () => {
  if (show !== 'none') {
    return;
  }

  if (timeOutRef.value !== undefined) {
    clearTimeout(timeOutRef.value);
  }

  timeOutRef.value = setTimeout(() => {
    openRef.value = true;
  }, 500);
};

const handleLeave = () => {
  if (show !== 'none') {
    return;
  }

  clearTimeout(timeOutRef.value);
  openRef.value = false;
};
</script>

<template>
  <HuiPopover class="relative w-fit z-10" :class="className" @mouseenter="handleEnter" @mouseleave="handleLeave">
    <Float
      :show="isOpen"
      :placement="position"
      :offset="15"
      :shift="6"
      :flip="10"
      :arrow="4"
      portal
      enter="transition duration-200 ease-out"
      enter-from="opacity-0 -translate-y-1"
      enter-to="opacity-100 translate-y-0"
      leave="transition duration-150 ease-in"
      leave-from="opacity-100 translate-y-0"
      leave-to="opacity-0 -translate-y-1"
    >
      <slot />

      <HuiPopoverPanel class="max-w-sm flex" static>
        <FloatArrow v-if="hasContentSlot" class="absolute bg-black w-4 h-4 rotate-45" />
        <div v-if="hasContentSlot" class="w-fit p-4 bg-black rounded-xl text-white text-sm">
          <slot name="content" />
        </div>
      </HuiPopoverPanel>
    </Float>
  </HuiPopover>
</template>
